// Форматирование чисел, денег и дат. Время показываем в поясе пользователя:
// из профиля (ручной выбор) или из браузера; по умолчанию Москва.
export const MSK = "Europe/Moscow";

let currentTimeZone = MSK;

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || MSK;
  } catch {
    return MSK;
  }
}

/** Пояс для всех дат интерфейса; AuthContext выставляет его после входа */
export function setTimeZone(tz: string | null | undefined): void {
  currentTimeZone = tz || browserTimeZone();
}

export function getTimeZone(): string {
  return currentTimeZone;
}

/** «Москва, UTC+3» для подписи «Время: …» */
export function timeZoneLabel(tz: string = currentTimeZone, at: Date = new Date()): string {
  const city = tz.split("/").pop()?.replace(/_/g, " ") ?? tz;
  const names: Record<string, string> = {
    Moscow: "Москва",
    Kaliningrad: "Калининград",
    Samara: "Самара",
    Yekaterinburg: "Екатеринбург",
    Omsk: "Омск",
    Novosibirsk: "Новосибирск",
    Krasnoyarsk: "Красноярск",
    Irkutsk: "Иркутск",
    Yakutsk: "Якутск",
    Vladivostok: "Владивосток",
    Magadan: "Магадан",
    Kamchatka: "Камчатка",
  };
  let offset = "";
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName")?.value;
    offset = part ? part.replace("GMT", "UTC") : "";
  } catch {
    offset = "";
  }
  return offset ? `${names[city] ?? city}, ${offset}` : (names[city] ?? city);
}

export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

export function pluralize(n: number, one: string, few: string, many: string): string {
  return `${n.toLocaleString("ru-RU")} ${plural(n, one, few, many)}`;
}

/** Цена игрока и бюджет в «млн»: 7 -> 7.0 млн, 12.8 -> 12.8 млн */
export function money(value: number | string | null | undefined, unit = true): string {
  if (value === null || value === undefined) return "—";
  const n = typeof value === "string" ? Number(value) : value;
  const text = n.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return unit ? `${text} млн` : text;
}

export function points(n: number): string {
  return pluralize(n, "очко", "очка", "очков");
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    timeZone: currentTimeZone,
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", {
    timeZone: currentTimeZone,
    day: "numeric",
    month: "short",
  });
}

/** «октября 2026 г.»: родительный падеж даёт формат с днём, день убираем */
export function formatMonthYear(iso: string): string {
  return new Date(iso)
    .toLocaleDateString("ru-RU", {
      timeZone: currentTimeZone,
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    .replace(/^\d+\s+/, "");
}

/** «через 14 ч», «через 2 д 4 ч», «через 35 мин», «прошёл» */
export function untilText(iso: string, now: Date = new Date()): string {
  const ms = new Date(iso).getTime() - now.getTime();
  if (ms <= 0) return "прошёл";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `через ${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `через ${hours} ч`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `через ${days} д ${restHours} ч` : `через ${days} д`;
}

export function deadlineText(iso: string, now: Date = new Date()): string {
  return `${formatDateTime(iso)} · ${untilText(iso, now)}`;
}

export function seasonLabel(year: number, sportCode?: string): string {
  // Хоккей и баскетбол играют сезон на стыке лет, футбол в Европе тоже
  void sportCode;
  return `${year}/${String(year + 1).slice(2)}`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0] ?? "");
  return letters.join("").toUpperCase() || "?";
}

export function placeText(rank: number | null | undefined): string {
  return rank ? `${rank}` : "—";
}
